/** Live Feed view component. */

import { fetchLiveFeedData } from '@/api/services/liveFeed';
import { showToast } from '@/utils/helpers';
import { logError } from '@/utils/errors';
import { escapeHTML, formatTimestamp, formatSentiment, formatPrice, formatPercent, formatLargeNumber } from '@/utils/formatters';
import type { Mention } from '@/types/responses';

let allMentions: Mention[] = [];
let lastRenderSignature = '';
let visibleRows = 200;

function renderLiveFeedErrorState(message: string): void {
  const container = document.getElementById('live-feed-table-container');
  if (!container) return;
  container.innerHTML = `<p class="text-red-400">${escapeHTML(message)}</p>`;
}

export type RenderLiveFeedOptions = {
  /** When true (e.g. Socket.IO refresh), keep scroll and “load more” slice instead of resetting to first 200 rows. */
  preserveScrollAndSlice?: boolean;
};

export async function renderLiveFeed(options?: RenderLiveFeedOptions): Promise<void> {
  if (!options?.preserveScrollAndSlice) {
    // Show live feed view and hide other views
    document.querySelectorAll('.view').forEach(v => {
      v.classList.add('hidden');
      (v as HTMLElement).style.display = 'none';
    });
    
    const liveFeedView = document.getElementById('view-live_feed');
    if (liveFeedView) {
      liveFeedView.classList.remove('hidden');
      liveFeedView.style.display = '';
    }
    
    // Hide loader
    const mainLoader = document.getElementById('main-loader');
    if (mainLoader) {
      mainLoader.classList.add('hidden');
      mainLoader.style.display = 'none';
      mainLoader.setAttribute('aria-busy', 'false');
    }
    
    // Update navigation store
    import('@/stores/navigation').then(module => {
      module.useNavigationStore.getState().setCurrentView('live_feed');
    });
    
    // Update active nav link
    document.querySelectorAll('.nav-link, .nav-link-mobile').forEach(link => {
      link.classList.remove('active');
      if (link.getAttribute('data-view') === 'live_feed') {
        link.classList.add('active');
      }
    });
  }
  
  try {
    const data = await fetchLiveFeedData();
    if (data === null) {
      // 304 — keep current table when we already have rows
      if (allMentions.length > 0) {
        return;
      }
      renderLiveFeedTable([]);
      return;
    }
    
    allMentions = data.mentions || [];
    if (options?.preserveScrollAndSlice && allMentions.length > 0) {
      visibleRows = Math.min(Math.max(visibleRows, 200), allMentions.length);
    } else {
      visibleRows = 200;
    }
    renderLiveFeedTable(allMentions);
    
    // Set up filter (only once)
    const filterInput = document.getElementById('live-feed-filter') as HTMLInputElement;
    if (filterInput && !filterInput.hasAttribute('data-listener-attached')) {
      filterInput.setAttribute('data-listener-attached', 'true');
      filterInput.addEventListener('input', handleFilterInput);
    }
    
  } catch (error) {
    logError(error, 'LiveFeed');
    renderLiveFeedErrorState('Failed to load live feed data. Please retry.');
    showToast('Failed to load live feed', true, { dedupeKey: 'live-feed-load-fail' });
  }
}

function handleFilterInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  const filterValue = input.value.toLowerCase();
  
  if (filterValue) {
    const filtered = allMentions.filter(m => m.ticker.toLowerCase().includes(filterValue));
    visibleRows = 200;
    renderLiveFeedTable(filtered);
  } else {
    visibleRows = 200;
    renderLiveFeedTable(allMentions);
  }
}

function renderLiveFeedTable(mentions: Mention[]): void {
  const container = document.getElementById('live-feed-table-container');
  if (!container) return;
  
  if (mentions.length === 0) {
    lastRenderSignature = '';
    container.innerHTML = '<p class="text-secondary">No mentions found.</p>';
    return;
  }
  
  const cappedMentions = mentions.slice(0, visibleRows);
  const signature = `${mentions.length}:${visibleRows}:${cappedMentions[0]?.timestamp ?? 'none'}:${cappedMentions[cappedMentions.length - 1]?.timestamp ?? 'none'}`;
  if (signature === lastRenderSignature) {
    return;
  }
  lastRenderSignature = signature;

  const prevScrollHeight = container.scrollHeight;
  const prevScrollTop = container.scrollTop;

  const table = createMentionsTable(cappedMentions);
  const hasMore = mentions.length > visibleRows;
  const moreButton = hasMore
    ? `<div class="mt-3 text-center"><button id="live-feed-load-more" class="btn btn-secondary">Load more (${mentions.length - visibleRows} remaining)</button></div>`
    : '';
  container.innerHTML = `${table}${moreButton}`;

  const heightDelta = container.scrollHeight - prevScrollHeight;
  if (prevScrollHeight > 0 && (prevScrollTop > 0 || heightDelta !== 0)) {
    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollTop = Math.max(0, Math.min(maxScroll, prevScrollTop + heightDelta));
  }

  if (hasMore) {
    const loadMoreBtn = document.getElementById('live-feed-load-more');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => {
        visibleRows += 200;
        renderLiveFeedTable(mentions);
      }, { once: true });
    }
  }
  
  // Click handlers are now handled globally via event delegation in app.ts
  // No need to add individual handlers here
}


// Unused function, may be used in future
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _createMentionRow(mention: Mention): string {
  const timestamp = formatTimestamp(mention.timestamp);
  // Check for null/undefined explicitly, but allow 0 values
  const sentiment = (mention.sentiment != null && !isNaN(mention.sentiment)) ? formatSentiment(mention.sentiment) : 'N/A';
  const price = (mention.price != null && !isNaN(mention.price)) ? formatPrice(mention.price) : 'N/A';
  const change = (mention.day_change != null && !isNaN(mention.day_change)) ? formatPercent(mention.day_change) : 'N/A';
  const karma = (mention.author_karma != null && !isNaN(mention.author_karma)) ? formatLargeNumber(mention.author_karma) : 'N/A';
  const sourceText = escapeHTML((mention.source_text || '').substring(0, 100));
  
  return `
    <td class="text-left p-2">${timestamp}</td>
    <td class="text-left p-2">
      <span class="font-bold text-neon-cyan cursor-pointer hover:underline" data-ticker="${escapeHTML(mention.ticker)}">
        ${escapeHTML(mention.ticker)}
      </span>
    </td>
    <td class="text-left p-2">${escapeHTML(mention.subreddit || 'N/A')}</td>
    <td class="text-left p-2 text-sm">${sourceText}${mention.source_text && mention.source_text.length > 100 ? '...' : ''}</td>
    <td class="text-right p-2">${sentiment}</td>
    <td class="text-right p-2">${price}</td>
    <td class="text-right p-2">${change}</td>
    <td class="text-right p-2">${karma}</td>
    <td class="text-left p-2">${escapeHTML(mention.author_name || '[deleted]')}</td>
  `;
}

function createMentionsTable(mentions: Mention[]): string {
  const headers = `
    <thead>
      <tr class="border-b border-primary">
        <th class="text-left p-2">Time</th>
        <th class="text-left p-2">Ticker</th>
        <th class="text-left p-2">Subreddit</th>
        <th class="text-left p-2">Text</th>
        <th class="text-right p-2">Sentiment</th>
        <th class="text-right p-2">Price</th>
        <th class="text-right p-2">Change</th>
        <th class="text-right p-2">Karma</th>
        <th class="text-left p-2">Author</th>
      </tr>
    </thead>
  `;
  
  const rows = mentions.map(mention => {
    const timestamp = formatTimestamp(mention.timestamp);
    // Check for null/undefined explicitly, but allow 0 values
    const sentiment = (mention.sentiment != null && !isNaN(mention.sentiment)) ? formatSentiment(mention.sentiment) : 'N/A';
    const price = (mention.price != null && !isNaN(mention.price)) ? formatPrice(mention.price) : 'N/A';
    const change = (mention.day_change != null && !isNaN(mention.day_change)) ? formatPercent(mention.day_change) : 'N/A';
    const karma = (mention.author_karma != null && !isNaN(mention.author_karma)) ? formatLargeNumber(mention.author_karma) : 'N/A';
    const sourceText = escapeHTML((mention.source_text || '').substring(0, 100));
    
    return `
      <tr class="border-b border-primary hover:bg-gray-700">
        <td class="text-left p-2">${timestamp}</td>
        <td class="text-left p-2">
          <span class="font-bold text-neon-cyan cursor-pointer hover:underline" data-ticker="${escapeHTML(mention.ticker)}">
            ${escapeHTML(mention.ticker)}
          </span>
        </td>
        <td class="text-left p-2">${escapeHTML(mention.subreddit || 'N/A')}</td>
        <td class="text-left p-2 text-sm">${sourceText}${mention.source_text && mention.source_text.length > 100 ? '...' : ''}</td>
        <td class="text-right p-2">${sentiment}</td>
        <td class="text-right p-2">${price}</td>
        <td class="text-right p-2">${change}</td>
        <td class="text-right p-2">${karma}</td>
        <td class="text-left p-2">${escapeHTML(mention.author_name || '[deleted]')}</td>
      </tr>
    `;
  }).join('');
  
  return `
    <table class="w-full text-sm">
      ${headers}
      <tbody>${rows}</tbody>
    </table>
  `;
}

// Helper functions are now imported from @/utils/formatters

